import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import BootstrapDropdown from 'react-bootstrap/Dropdown';

import OptionsList from './DropdownOptionsList';
import Select from './DropdownSelect';
import FieldError from '../FieldError';
import FieldHint from '../FieldHint';
import { getLabel } from '../utils';

import '../../shared/dropdown.scss';
import '../form.scss';

const Dropdown = ({
    isLoading, options, hint, error, label, required, value, ref, ...inputProps
}) => {
    const selectRef = React.useRef(null);
    const resolvedRef = ref || selectRef;

    const handleOptionSelect = React.useCallback((id) => {
        resolvedRef.current.value = id;
        resolvedRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }, [resolvedRef]);

    const selectedLabel = React.useMemo(() => {
        if (!value) {
            return <>&nbsp;</>;
        }

        const selectedOption = options.find((o) => o.id === value);
        return selectedOption.title;
    }, [options, value]);

    const labelText = getLabel(label, inputProps.name, required);

    const hasError = error && error.length > 0;

    const fieldClass = classNames({
        'ml-form-field': true,
        'ml-form-field-select': true,
        'error': hasError,
    });

    return (
        <div className={fieldClass}>
            <div className="select">
                <span>{ labelText }</span>
                <BootstrapDropdown
                    key={`dropdown-label-${inputProps.name}`}
                >
                    <BootstrapDropdown.Toggle
                        key={`dropdown-label-${inputProps.name}`}
                        variant="primary"
                        className="select-styled"
                        title={labelText}
                    >
                        { selectedLabel }
                    </BootstrapDropdown.Toggle>
                    <OptionsList
                        isLoading={isLoading}
                        label={labelText}
                        options={options}
                        onOptionSelect={handleOptionSelect}
                    />
                </BootstrapDropdown>
                <FieldHint hint={hint} />
                <FieldError error={error} />

                <Select
                    key={`dropdown-select-${inputProps.name}`}
                    ref={resolvedRef}
                    options={options}
                    value={value}
                    {...inputProps}
                />
            </div>
        </div>
    );
};

Dropdown.propTypes = {
    isLoading: PropTypes.bool,
    name: PropTypes.string,
    label: PropTypes.string,
    hint: PropTypes.string,
    required: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        title: PropTypes.string.isRequired,
    })).isRequired,
    error: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
};

export default React.memo(Dropdown);
